/**
 * Outcome tracking polish (docs/ux/project-tab-spec.md Phase 3 `outcome-polish`): the redirect
 * preview (what moves or stays before the user commits), the re-triage diff (what the new
 * outcome changed), the history timeline (both outcomes after a redirect) and the full-feed
 * search. Pure functions over the decrypted item plus the same join the aggregate uses.
 */
import type { BarkAction, BarkRecord, BarkTarget } from '@viko/protocol'
import { type AggContext, taskState } from './bark-aggregate'

export interface PreviewLine {
  tone: 'move' | 'keep' | 'note'
  text: string
}

const STARTED = new Set(['running', 'verifying', 'landed', 'needs-you', 'blocked', 'failed'])

function targetLabel(t: BarkTarget, ctx: AggContext): string {
  if (t.type === 'task') return `"${ctx.tasks.get(t.id)?.title ?? 'a task'}"`
  if (t.type === 'run') return `"${ctx.runs.get(t.id)?.title ?? 'a live run'}"`
  if (t.type === 'workstream') return 'a new lane'
  if (t.type === 'decision') return 'the question for you'
  return t.type
}

/** What a redirect (or undo) would do to the active branches, before the user commits. */
export function redirectPreview(b: BarkRecord, ctx: AggContext): PreviewLine[] {
  const out: PreviewLine[] = []
  for (const a of b.actions.filter((x) => x.status === 'active'))
    for (const op of a.ops) {
      if (op.op === 'task.created') {
        const task = ctx.tasks.get(op.taskId)
        const run = task?.runId ? ctx.runs.get(task.runId) : undefined
        const s = taskState(task, run)
        out.push(
          STARTED.has(s)
            ? {
                tone: 'keep',
                text: `${targetLabel({ type: 'task', id: op.taskId }, ctx)} already started — it stays (use Cancel to stop it)`,
              }
            : {
                tone: 'move',
                text: `Cancels ${targetLabel({ type: 'task', id: op.taskId }, ctx)} and redoes it where you say`,
              },
        )
      } else if (op.op === 'task.updated')
        out.push({
          tone: 'move',
          text: `Restores ${targetLabel({ type: 'task', id: op.taskId }, ctx)} (${Object.keys(op.before).join(', ') || 'fields'})`,
        })
      else if (op.op === 'run.steered')
        out.push({
          tone: 'note',
          text: `Tells ${targetLabel({ type: 'run', id: op.runId }, ctx)} to disregard your message (a steer cannot be unsent)`,
        })
      else if (op.op === 'run.started')
        out.push({
          tone: 'keep',
          text: `${targetLabel({ type: 'run', id: op.runId }, ctx)} keeps running`,
        })
      else if (op.op === 'workstream.created')
        out.push({ tone: 'move', text: 'Archives the lane it created, if nothing else is in it' })
      else if (op.op === 'decision.created')
        out.push({ tone: 'move', text: 'Withdraws its question from your Inbox' })
    }
  if (!out.length)
    out.push({
      tone: 'note',
      text: 'Nothing to move yet; Viko handles it again with your instruction',
    })
  return out
}

export interface OutcomeDiff {
  /** The instruction or kind that caused the revision. */
  cause: string
  at: number
  removed: string[]
  added: string[]
}

const actionTargets = (actions: BarkAction[], ctx: AggContext) =>
  actions.flatMap((a) =>
    a.targets.length
      ? a.targets.map((t) => `${a.decision}: ${targetLabel(t, ctx)}`)
      : [a.decision === 'answered' ? 'answered' : a.decision],
  )

/** The latest redirect/re-triage: which targets were replaced by which. */
export function retriageDiff(b: BarkRecord, ctx: AggContext): OutcomeDiff | undefined {
  const rev = [...b.history]
    .reverse()
    .find((h) => (h.kind === 'redirect' || h.kind === 'retriage') && h.by === 'user')
  if (!rev) return undefined
  const before = b.actions.filter((a) => a.at < rev.at && a.status === 'undone')
  const after = b.actions.filter((a) => a.at >= rev.at && a.status === 'active')
  const was = new Set(actionTargets(before, ctx))
  const now = new Set(actionTargets(after, ctx))
  return {
    cause: rev.note ?? (rev.kind === 'retriage' ? 'Re-triage' : 'Redirect'),
    at: rev.at,
    removed: [...was].filter((x) => !now.has(x)),
    added: [...now].filter((x) => !was.has(x)),
  }
}

export interface TimelineEntry {
  at: number
  who: 'you' | 'Viko' | 'Viko (system)'
  text: string
  tone: 'ok' | 'wait' | 'fail' | 'idle'
}

const KIND_TEXT: Record<string, string> = {
  created: 'Barked',
  triaged: 'Viko picked it up',
  proposal: 'Proposal',
  confirmed: 'Confirmed',
  action: 'Outcome',
  redirect: 'Redirected',
  undo: 'Undone',
  retriage: 'Re-triaged',
  context: 'Added context',
  cancel: 'Cancelled',
  split: 'Split',
  merge: 'Merged',
  move: 'Moved messages',
  failed: 'Failed',
}

/** History as a readable timeline; a redirect shows both the old and the new outcome. */
export function historyTimeline(b: BarkRecord): TimelineEntry[] {
  return b.history.map((h) => ({
    at: h.at,
    who: h.by === 'user' ? 'you' : h.by === 'agent' ? 'Viko' : 'Viko (system)',
    text: `${KIND_TEXT[h.kind] ?? h.kind}${h.note ? ` — ${h.note}` : ''}`,
    tone:
      h.kind === 'failed'
        ? 'fail'
        : h.kind === 'undo' || h.kind === 'cancel'
          ? 'idle'
          : h.kind === 'redirect' || h.kind === 'retriage'
            ? 'wait'
            : 'ok',
  }))
}

/** Full-feed search: the words, title, interpretation, answers and conversation. */
export function searchBarks<T extends { bark: BarkRecord }>(rows: T[], query: string): T[] {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean)
  if (!terms.length) return rows
  return rows.filter(({ bark: b }) => {
    const hay = [
      b.text,
      b.title,
      b.interpretation,
      b.topicSummary,
      ...b.thread.map((m) => m.text),
      ...b.actions.map((a) => a.answer?.text),
      ...b.proposals.map((p) => p.title),
    ]
      .filter(Boolean)
      .join('\n')
      .toLowerCase()
    return terms.every((t) => hay.includes(t))
  })
}
